/**
 * PATH: packages/bills/src/components/BillJournalPreview.tsx
 * FILE_ID: BILLS-PKG-CMP-JE-001
 * PURPOSE: Show the balanced double-entry a bill WILL post (p3c) before
 *          the operator pushes it to Xero — DR expense per account, DR
 *          GST, CR Accounts Payable — with a clear balanced / out-of-
 *          balance signal.
 *
 * WHY: Xero stays the GL; this is the "what am I about to book?" preview.
 *      Surfacing the Σdebit==Σcredit check here catches a mis-coded bill
 *      (lines + GST don't equal the supplier total) before it becomes a
 *      draft in Xero that someone has to unpick later.
 *
 * DEPENDENCIES: react + ../types + ../billJournal (pure) + ../coaOptions
 *               (account name lookup). Transport-free — the bill + CoA
 *               are passed in by the host.
 */

import { useMemo } from 'react';
import type { BillDetail, CoaAccount } from '../types';
import { buildBillJournal } from '../billJournal';
import { accountLabel } from '../coaOptions';

interface Props {
  /** Accepts the panel's ``merged`` draft (Partial) — the util null-guards. */
  bill: Partial<BillDetail>;
  accounts?: CoaAccount[] | null;
}

export function BillJournalPreview({ bill, accounts }: Props) {
  const nameByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of accounts ?? []) {
      if (a.code) m.set(a.code.trim(), accountLabel(a));
    }
    return m;
  }, [accounts]);

  const journal = useMemo(
    () => buildBillJournal(bill, { accountName: (c) => nameByCode.get(c) }),
    [bill, nameByCode],
  );

  const fmt = (n: number) =>
    n
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : '';

  return (
    <details className="mb-2.5 rounded-lg border border-border bg-card shadow-sm" open>
      <summary className="flex cursor-pointer select-none items-center justify-between gap-2 px-3 py-2 text-xs font-semibold tracking-tight text-foreground">
        <span>Journal preview</span>
        <span
          className={[
            'rounded-full px-1.5 py-0.5 text-[11px] font-medium',
            journal.balanced
              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
          ].join(' ')}
          title="Σ debits must equal Σ credits"
        >
          {journal.balanced ? 'Balanced' : `Out by ${fmt(Math.abs(journal.variance))}`}
        </span>
      </summary>

      <div className="overflow-x-auto border-t border-border">
        <table className="w-full min-w-[420px] text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="px-2 py-1 text-left font-medium">Account</th>
              <th className="px-2 py-1 text-right font-medium">Debit</th>
              <th className="px-2 py-1 text-right font-medium">Credit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {journal.lines.map((l, i) => (
              <tr key={`${l.account}-${i}`} className="hover:bg-accent/30">
                <td className="px-2 py-1">
                  <span className="text-foreground">{l.account_name || l.account}</span>
                  {l.description && (
                    <span className="ml-1 text-muted-foreground">· {l.description}</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt(l.debit)}</td>
                <td className="px-2 py-1 text-right tabular-nums">{fmt(l.credit)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-border bg-muted/30 font-semibold">
              <td className="px-2 py-1 text-right text-[11px] uppercase tracking-wide text-muted-foreground">
                Totals ({journal.currency})
              </td>
              <td className="px-2 py-1 text-right tabular-nums">{fmt(journal.total_debit)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{fmt(journal.total_credit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {!journal.balanced && (
        <p className="px-2 py-1.5 text-[11px] text-red-700 dark:text-red-300">
          Coded lines + GST don&apos;t reconcile to the supplier total. Fix the
          line coding or the totals before pushing to Xero.
        </p>
      )}
    </details>
  );
}
